<template>
  <header
    class="app-header"
    :class="authed ? 'app-header--authed' : 'app-header--guest'"
  >
    <div class="up-container app-header__inner">
      <UPlusLogo @click="goHome" />

      <nav class="app-header__nav" aria-label="主要导航">
        <button
          v-for="item in items"
          :key="item.key"
          type="button"
          :class="{ active: active === item.key }"
          @click="select(item)"
        >
          {{ $t(item.label) }}
        </button>
      </nav>

      <div v-if="authed" class="app-header__tools">
        <label class="app-header__search">
          <i class="el-icon-search" aria-hidden="true" />
          <input
            :value="search"
            :placeholder="$t('搜索策略 / 币种')"
            :aria-label="$t('搜索策略 / 币种')"
            @input="$emit('update:search', $event.target.value)"
          />
        </label>

        <el-dropdown
          trigger="click"
          :disabled="exchangeLoading"
          @command="$emit('exchange', $event)"
        >
          <button
            class="app-header__exchange"
            type="button"
            :disabled="exchangeLoading"
            :aria-busy="exchangeLoading ? 'true' : 'false'"
          >
            <span class="status-dot" />
            {{ exchange }}
            <i
              :class="exchangeLoading ? 'el-icon-loading' : 'el-icon-arrow-down'"
              aria-hidden="true"
            />
          </button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item
              v-for="item in exchangeOptions"
              :key="item.command"
              :command="item.command"
              :class="{ 'exchange-option--active': item.label === exchange }"
            >
              <i v-if="item.label === exchange" class="el-icon-check" />
              <span v-else class="exchange-option__placeholder" />
              {{ item.label }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>

        <el-dropdown
          ref="mobileExchangeDropdown"
          class="app-header__mobile-exchange-dropdown"
          trigger="click"
          placement="bottom-end"
          :disabled="exchangeLoading"
          @visible-change="handleMobileExchangeVisible"
          @command="selectMobileNavigation"
        >
          <button
            class="app-header__mobile-exchange"
            type="button"
            :disabled="exchangeLoading"
            :aria-busy="exchangeLoading ? 'true' : 'false'"
            :aria-label="$t('切换交易所')"
            :title="exchange"
          >
            <span class="status-dot" :class="{ 'is-muted': !exchangeOptions.length }" />
            <span class="app-header__mobile-exchange-label">
              {{ exchangeLoading ? $t('加载中…') : exchange }}
            </span>
            <i
              :class="exchangeLoading ? 'el-icon-loading' : 'el-icon-arrow-down'"
              aria-hidden="true"
            />
          </button>
          <el-dropdown-menu slot="dropdown" class="app-header-mobile-exchange-menu">
            <li class="app-header-mobile-exchange-menu__heading" role="presentation">
              <strong>{{ $t('切换交易所') }}</strong>
              <small>{{ $t('仅显示所选交易所的机器人与收益数据') }}</small>
            </li>
            <el-dropdown-item
              v-for="option in exchangeOptions"
              :key="`header-mobile-exchange-${option.command}`"
              :command="`__exchange:${option.command}`"
              :disabled="exchangeLoading || option.label === exchange"
              :class="{ 'exchange-option--active': option.label === exchange }"
            >
              <img
                v-if="option.logo"
                class="app-header-mobile-exchange-menu__logo"
                :src="option.logo"
                :alt="option.label"
              />
              <span class="app-header-mobile-exchange-menu__copy">
                <strong>{{ option.label }}</strong>
                <small>{{ $t(option.bound ? '已绑定 API · 可运行策略' : '未绑定 API') }}</small>
              </span>
              <i v-if="option.label === exchange" class="el-icon-check" aria-hidden="true" />
            </el-dropdown-item>
            <el-dropdown-item
              v-if="!exchangeLoading && !exchangeOptions.length && exchangeError"
              command="__retry-exchanges"
              class="mobile-exchange-retry"
            >
              <i class="el-icon-refresh" aria-hidden="true" />
              <span>{{ $t('重新加载交易所') }}</span>
            </el-dropdown-item>
            <el-dropdown-item
              v-else-if="!exchangeLoading && !exchangeOptions.length"
              disabled
              class="mobile-exchange-empty"
            >
              <span>{{ $t('暂无可用交易所') }}</span>
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>

        <el-popover
          v-model="notificationVisible"
          placement="bottom-end"
          :width="320"
          :offset="0"
          trigger="click"
          popper-class="app-header-notification-popover"
        >
          <div :key="`notification-${language}`" class="notification">
            <div class="notification__title">
              <strong class="notification__title-desktop">{{ notificationCenterTitle }}</strong>
              <strong class="notification__title-mobile" style="border-bottom: unset !important;">{{ notificationTitle }}</strong>
              <span v-if="notificationCount">{{ notificationUnreadLabel }}</span>
            </div>
            <div v-if="notificationLoading" class="notification__state">
              <i class="el-icon-loading" aria-hidden="true" />
              {{ $t('正在读取通知…') }}
            </div>
            <template v-else-if="previewNotifications.length">
              <button
                v-for="(item, index) in previewNotifications"
                :key="item.id"
                :class="['notification__item', { 'notification__item--mobile-hidden': index >= 2 }]"
                type="button"
                @click="selectNotification(item)"
              >
                <span
                  v-if="item.unread"
                  class="notification__dot"
                  :class="notificationTone(item)"
                />
                <span class="notification__content">
                  <strong v-raw-text="item.title" />
                  <small
                    v-raw-text="item.preview || $t('点击查看通知详情')"
                    class="notification__preview"
                  />
                  <span
                    v-raw-text="notificationMobilePreview(item)"
                    class="notification__mobile-copy"
                  />
                  <time
                    v-if="item.sendTime"
                    v-raw-text="item.sendTime"
                    class="notification__time"
                  />
                </span>
                <i class="el-icon-arrow-right notification__arrow" aria-hidden="true" />
              </button>
            </template>
            <div v-else class="notification__state">{{ $t('暂无通知') }}</div>
            <button class="notification__read-all" type="button" @click="markAllRead">
              {{ $t('全部标记为已读') }}
            </button>
            <router-link
              class="notification__more"
              to="/app/notifications"
              @click.native="notificationVisible = false"
            >
              {{ $t('查看更多') }}
              <i class="el-icon-arrow-right" aria-hidden="true" />
            </router-link>
          </div>
          <button
            slot="reference"
            class="app-header__icon"
            type="button"
            :aria-label="$t(notificationCount ? `通知，${notificationCount} 条未读` : '通知')"
          >
            <i class="el-icon-bell" aria-hidden="true" />
            <span v-if="notificationCount" class="app-header__badge" />
          </button>
        </el-popover>

        <el-dropdown ref="languageDropdown" trigger="click" @visible-change="languageMenuVisible = $event" @command="changeLanguage">
          <button class="app-header__language" type="button">
            <img
              class="app-header__language-icon"
              src="@/assets/img/footer/language.png"
              alt=""
              aria-hidden="true"
            />
            <span class="app-header__language-label app-header__language-label--full" v-raw-text="languageLabel" />
            <span class="app-header__language-label app-header__language-label--short" v-raw-text="languageShortLabel" />
            <i class="el-icon-arrow-down" aria-hidden="true" />
          </button>
          <el-dropdown-menu slot="dropdown" class="app-header-language-menu">
            <li class="app-header-sheet__heading" role="presentation">选择语言</li>
            <el-dropdown-item
              v-for="option in languageOptions"
              :key="option.code"
              :command="option.code"
              :class="{ 'is-active': option.code === language }"
            >
              <span v-raw-text="option.label" />
              <i v-if="option.code === language" class="el-icon-check" aria-hidden="true" />
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>

        <el-popover
          v-model="userMenuVisible"
          placement="bottom-end"
          :width="264"
          :offset="0"
          :trigger="mobileHeaderViewport ? 'manual' : 'click'"
          transition="app-header-user-sheet"
          popper-class="app-header-user-popover"
        >
          <div class="app-header__user-menu">
            <div class="app-header__user-summary">
              <span class="app-header__menu-avatar">
                <img
                  v-if="userAvatar && !avatarLoadFailed"
                  :src="userAvatar"
                  alt=""
                  @error="avatarLoadFailed = true"
                />
                <span v-else v-raw-text="userInitial" />
              </span>
              <span>
                <b v-raw-text="userName" />
                <small v-if="userInvitation">{{ $t('邀请码') }} {{ userInvitation }}</small>
                <small v-else v-raw-text="maskedUserAccount" />
              </span>
            </div>

            <button
              class="app-header__user-menu-item app-header__user-menu-item--api"
              type="button"
              @click="selectUserMenu('api-binding')"
            >
              <span class="app-header__menu-icon app-header__menu-icon--api">
                <img src="@/assets/icons/user-menu-api.svg" alt="" aria-hidden="true" />
              </span>
              <span class="app-header__menu-copy">
                <strong>交易所 API 绑定</strong>
                <small>机器人运行的前提</small>
              </span>
              <span class="app-header__binding-status">
                <i aria-hidden="true" />
                {{ apiBoundCount }}/{{ apiTotalCount }} 已绑定
              </span>
            </button>

            <button
              class="app-header__user-menu-item app-header__user-menu-item--desktop-profile"
              type="button"
              @click="selectUserMenu('profile')"
            >
              <span class="app-header__menu-icon">
                <img src="@/assets/icons/user-menu-profile.svg" alt="" aria-hidden="true" />
              </span>
              <span class="app-header__menu-copy">
                <strong>个人中心</strong>
              </span>
              <i class="el-icon-arrow-right app-header__menu-arrow" aria-hidden="true" />
            </button>

            <button class="app-header__user-menu-item app-header__user-menu-item--mobile" type="button" @click="selectUserMenu('personal-info')">
              <span class="app-header__menu-copy"><strong>个人资料</strong></span>
              <span class="app-header__menu-value" v-raw-text="userName" />
              <i class="el-icon-arrow-right app-header__menu-arrow" aria-hidden="true" />
            </button>
            <button class="app-header__user-menu-item app-header__user-menu-item--mobile" type="button" @click="selectUserMenu('password')">
              <span class="app-header__menu-copy"><strong>修改登录密码</strong></span>
              <i class="el-icon-arrow-right app-header__menu-arrow" aria-hidden="true" />
            </button>
            <button class="app-header__user-menu-item app-header__user-menu-item--mobile" type="button" @click="selectUserMenu('help')">
              <span class="app-header__menu-copy"><strong>帮助中心</strong></span>
              <i class="el-icon-arrow-right app-header__menu-arrow" aria-hidden="true" />
            </button>
            <button class="app-header__user-menu-item app-header__user-menu-item--mobile" type="button" @click="selectUserMenu('legal')">
              <span class="app-header__menu-copy"><strong>服务条款与隐私</strong></span>
              <span class="app-header__menu-value">v3.2</span>
              <i class="el-icon-arrow-right app-header__menu-arrow" aria-hidden="true" />
            </button>

            <button
              class="app-header__user-menu-item app-header__user-menu-item--logout"
              type="button"
              @click="selectUserMenu('logout')"
            >
              <span class="app-header__menu-icon app-header__menu-icon--logout">
                <i class="el-icon-switch-button" aria-hidden="true" />
              </span>
              <span class="app-header__menu-copy">
                <strong>{{ $t('退出登录') }}</strong>
              </span>
            </button>
          </div>

          <button
            slot="reference"
            class="app-header__user"
            type="button"
            :aria-expanded="String(userMenuVisible)"
            aria-haspopup="menu"
            @click="handleUserMenuTrigger"
          >
            <span class="app-header__avatar">
              <img
                v-if="userAvatar && !avatarLoadFailed"
                :src="userAvatar"
                alt=""
                @error="avatarLoadFailed = true"
              />
              <span v-else v-raw-text="userInitial" />
            </span>
            <span class="app-header__identity">
              <b v-raw-text="userName" />
              <small>PRO</small>
            </span>
          </button>
        </el-popover>

      </div>

      <div v-else class="app-header__auth">
        <el-dropdown ref="languageDropdown" trigger="click" @visible-change="languageMenuVisible = $event" @command="changeLanguage">
          <button class="app-header__language" type="button">
            <img
              class="app-header__language-icon"
              src="@/assets/img/footer/language.png"
              alt=""
              aria-hidden="true"
            />
            <span class="app-header__language-label app-header__language-label--full" v-raw-text="languageLabel" />
            <span class="app-header__language-label app-header__language-label--short" v-raw-text="languageShortLabel" />
            <i class="el-icon-arrow-down" aria-hidden="true" />
          </button>
          <el-dropdown-menu slot="dropdown" class="app-header-language-menu">
            <li class="app-header-sheet__heading" role="presentation">选择语言</li>
            <el-dropdown-item
              v-for="option in languageOptions"
              :key="`guest-language-${option.code}`"
              :command="option.code"
              :class="{ 'is-active': option.code === language }"
            >
              <span v-raw-text="option.label" />
              <i v-if="option.code === language" class="el-icon-check" aria-hidden="true" />
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
        <button
          class="app-header__download"
          type="button"
          :aria-label="$t('下载 App')"
          @click="openAppDownload"
        >
          <i class="el-icon-download" aria-hidden="true" />
          <span>{{ $t('下载 App') }}</span>
        </button>
        <button class="up-ghost-button" type="button" @click="$router.push('/login')">
          登录
        </button>
        <button class="up-primary-button" type="button" @click="$router.push('/register')">
          免费注册
        </button>
      </div>

      <el-dropdown
        class="app-header__mobile-nav-dropdown"
        trigger="click"
        placement="bottom-end"
        @command="selectMobileNavigation"
      >
        <button
          class="app-header__mobile-nav-trigger"
          type="button"
          :aria-label="$t('打开导航菜单')"
          :title="activeNavigationLabel"
        >
          <i v-if="authed" class="el-icon-menu" aria-hidden="true" />
          <img
            v-else
            class="app-header__mobile-nav-image"
            src="@/assets/rebuild/mobile-menu.svg"
            alt=""
            aria-hidden="true"
          >
        </button>
        <el-dropdown-menu slot="dropdown" class="app-header-mobile-nav-menu">
          <template v-if="authed">
            <el-dropdown-item disabled class="mobile-exchange-title">
              <span>{{ $t('交易所') }}</span>
              <strong>{{ exchangeLoading ? $t('加载中…') : exchange }}</strong>
            </el-dropdown-item>
            <el-dropdown-item
              v-for="option in exchangeOptions"
              :key="`mobile-exchange-${option.command}`"
              :command="`__exchange:${option.command}`"
              :disabled="exchangeLoading || option.label === exchange"
              :class="{
                'mobile-exchange-option': true,
                'exchange-option--active': option.label === exchange
              }"
            >
              <span class="status-dot" aria-hidden="true" />
              <span>{{ option.label }}</span>
              <i
                v-if="option.label === exchange"
                class="el-icon-check"
                aria-hidden="true"
              />
            </el-dropdown-item>
            <el-dropdown-item
              v-if="!exchangeLoading && !exchangeOptions.length && exchangeError"
              command="__retry-exchanges"
              class="mobile-exchange-retry"
            >
              <i class="el-icon-refresh" aria-hidden="true" />
              <span>{{ $t('重新加载交易所') }}</span>
            </el-dropdown-item>
            <el-dropdown-item
              v-else-if="!exchangeLoading && !exchangeOptions.length"
              disabled
              class="mobile-exchange-empty"
            >
              <span>{{ $t('暂无可用交易所') }}</span>
            </el-dropdown-item>
          </template>
          <el-dropdown-item
            v-for="(item, index) in items"
            :key="item.key"
            :command="item.key"
            :divided="authed && index === 0"
            :class="{ 'is-active': active === item.key }"
          >
            <span>{{ $t(item.label) }}</span>
            <i v-if="active === item.key" class="el-icon-check" aria-hidden="true" />
          </el-dropdown-item>
          <template v-if="!authed">
            <el-dropdown-item
              v-for="(option, languageIndex) in languageOptions"
              :key="`mobile-language-${option.code}`"
              :command="`__language:${option.code}`"
              :divided="languageIndex === 0"
              :class="{ 'is-active': option.code === language }"
            >
              <span v-raw-text="option.label" />
              <i v-if="option.code === language" class="el-icon-check" aria-hidden="true" />
            </el-dropdown-item>
            <el-dropdown-item
              command="__download-app"
              class="app-header-mobile-nav-menu__action app-header-mobile-nav-menu__action--download"
            >
              <span>{{ $t('下载 App') }}</span>
              <i class="el-icon-download" aria-hidden="true" />
            </el-dropdown-item>
            <el-dropdown-item command="__login" class="app-header-mobile-nav-menu__action">
              <span>{{ $t('登录') }}</span>
              <i class="el-icon-user" aria-hidden="true" />
            </el-dropdown-item>
            <el-dropdown-item command="__register" class="app-header-mobile-nav-menu__action is-primary">
              <span>{{ $t('免费注册') }}</span>
              <i class="el-icon-circle-plus-outline" aria-hidden="true" />
            </el-dropdown-item>
          </template>
        </el-dropdown-menu>
      </el-dropdown>
    </div>
    <transition name="app-header-sheet-backdrop">
      <button
        v-if="authed && (notificationVisible || userMenuVisible || languageMenuVisible || mobileExchangeMenuVisible)"
        class="app-header__sheet-backdrop"
        type="button"
        aria-label="关闭弹框"
        @click="closeMobileHeaderSheets"
      />
    </transition>
  </header>
</template>

<script>
import UPlusLogo from './UPlusLogo.vue'
import { navigateFromLogo } from '@/utils/logo-navigation'
import {
  LANGUAGE_OPTIONS,
  languageLabel as getLanguageLabel,
  readLanguage,
  saveLanguage
} from '@/utils/locale'
import { translateText } from '@/i18n'
import { rawText } from '@/directives/raw-content'

export default {
  name: 'AppHeader',
  components: { UPlusLogo },
  directives: { rawText },
  props: {
    active: { type: String, default: 'overview' },
    authed: { type: Boolean, default: false },
    exchange: { type: String, default: '交易所加载中' },
    exchangeOptions: {
      type: Array,
      default: () => []
    },
    exchangeLoading: { type: Boolean, default: false },
    exchangeError: { type: String, default: '' },
    search: { type: String, default: '' },
    userName: { type: String, default: 'UPlus 用户' },
    userAccount: { type: String, default: '' },
    userAvatar: { type: String, default: '' },
    userInitial: { type: String, default: 'U' },
    userInvitation: { type: [String, Number], default: '' },
    apiBoundCount: { type: Number, default: 0 },
    apiTotalCount: { type: Number, default: 0 },
    notifications: { type: Array, default: () => [] },
    notificationCount: { type: Number, default: 0 },
    notificationLoading: { type: Boolean, default: false }
  },
  data() {
    return {
      items: [
        { key: 'overview', label: '总览' },
        { key: 'robots', label: '机器人' },
        { key: 'earnings', label: '收益' },
        { key: 'assets', label: '资产' },
        { key: 'bills', label: '账单' },
        { key: 'invite', label: '邀请返现' }
      ],
      language: readLanguage(),
      languageOptions: LANGUAGE_OPTIONS,
      notificationVisible: false,
      userMenuVisible: false,
      languageMenuVisible: false,
      mobileExchangeMenuVisible: false,
      avatarLoadFailed: false,
      mobileHeaderViewport: typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches,
      mobileUserMenuPinned: false
    }
  },
  created() {
    this.$root.$on('mobile-account-child-closed', this.releaseMobileUserMenuPin)
  },
  mounted() {
    window.addEventListener('resize', this.syncMobileHeaderViewport)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.syncMobileHeaderViewport)
    this.$root.$off('mobile-account-child-closed', this.releaseMobileUserMenuPin)
  },
  watch: {
    userAvatar() {
      this.avatarLoadFailed = false
    }
  },
  computed: {
    notificationCenterTitle() {
      return translateText('通知中心', this.language)
    },
    notificationTitle() {
      return translateText('通知', this.language)
    },
    notificationUnreadLabel() {
      return translateText(`${this.notificationCount} 条未读`, this.language)
    },
    languageLabel() {
      return getLanguageLabel(this.language)
    },
    languageShortLabel() {
      return {
        'zh-CN': '简中',
        'zh-HK': '繁中',
        en: 'EN',
        ko: '한국어'
      }[this.language] || getLanguageLabel(this.language)
    },
    previewNotifications() {
      return this.notifications
        .filter(item => Number(item && item.type) === 7)
        .slice(0, 4)
    },
    maskedUserAccount() {
      const account = String(this.userAccount || '')
      if (!account) return '--'
      if (!account.includes('@')) {
        if (account.length <= 7) return account
        return `${account.slice(0, 3)}***${account.slice(-3)}`
      }
      const [local, domain] = account.split('@')
      const suffix = local.length > 3 ? local.slice(-3) : ''
      return `${local.slice(0, 1)}***${suffix}@${domain}`
    },
    activeNavigationLabel() {
      const item = this.items.find(entry => entry.key === this.active)
      return item ? item.label : this.$t('导航菜单')
    }
  },
  methods: {
    changeLanguage(language) {
      this.language = saveLanguage(language)
      this.languageMenuVisible = false
      this.$emit('language-change', this.language)
    },
    goHome() {
      navigateFromLogo(this.$router, this.authed ? '/app/overview' : '/')
    },
    select(item) {
      if (this.authed) {
        this.$emit('navigate', item.key)
      } else if (item.key !== 'overview') {
        this.$emit('locked', item.key)
      }
    },
    openAppDownload() {
      this.$root.$emit('open-app-download')
    },
    selectMobileNavigation(command) {
      if (command === '__retry-exchanges') {
        this.$emit('exchange-retry')
        return
      }
      if (String(command).startsWith('__exchange:')) {
        if (this.exchangeLoading) return
        const exchangeCommand = String(command).slice('__exchange:'.length)
        const option = this.exchangeOptions.find(item => String(item.command) === exchangeCommand)
        if (option && option.label !== this.exchange) this.$emit('exchange', option.command)
        return
      }
      if (String(command).startsWith('__language:')) {
        this.changeLanguage(String(command).slice('__language:'.length))
        return
      }
      if (command === '__download-app') {
        this.openAppDownload()
        return
      }
      if (command === '__login') {
        this.$router.push('/login')
        return
      }
      if (command === '__register') {
        this.$router.push('/register')
        return
      }
      const item = this.items.find(entry => entry.key === command)
      if (item) this.select(item)
    },
    handleMobileExchangeVisible(visible) {
      this.mobileExchangeMenuVisible = visible
      if (!visible || !this.isMobileViewport()) return
      this.notificationVisible = false
      this.userMenuVisible = false
      this.languageMenuVisible = false
      const dropdown = this.$refs.languageDropdown
      const instance = Array.isArray(dropdown) ? dropdown[0] : dropdown
      if (instance && typeof instance.hide === 'function') instance.hide()
    },
    selectUserMenu(command) {
      const mobileChildCommands = [
        'api-binding',
        'personal-info',
        'password',
        'help',
        'legal',
        'logout'
      ]
      const keepMobileParent = this.isMobileViewport() && mobileChildCommands.includes(command)
      this.mobileUserMenuPinned = keepMobileParent
      if (!keepMobileParent) this.userMenuVisible = false
      if (command === 'api-binding') {
        this.$emit('api-binding')
        return
      }
      if (command === 'logout') {
        this.$emit('logout')
        return
      }
      if (command === 'help') {
        this.$root.$emit('open-help-center')
        return
      }
      if (command === 'legal') {
        this.$root.$emit('open-legal-terms', 'terms')
        return
      }
      this.$emit('profile', command)
    },
    isMobileViewport() {
      return typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches
    },
    syncMobileHeaderViewport() {
      this.mobileHeaderViewport = this.isMobileViewport()
      if (!this.mobileHeaderViewport) this.mobileUserMenuPinned = false
    },
    handleUserMenuTrigger() {
      if (!this.mobileHeaderViewport) return
      this.notificationVisible = false
      this.languageMenuVisible = false
      this.userMenuVisible = !this.userMenuVisible
      if (!this.userMenuVisible) this.mobileUserMenuPinned = false
    },
    releaseMobileUserMenuPin() {
      if (!this.mobileUserMenuPinned || !this.isMobileViewport()) return
      this.mobileUserMenuPinned = false
    },
    markAllRead() {
      this.notificationVisible = false
      this.$emit('notification-read-all')
    },
    closeMobileHeaderSheets() {
      this.notificationVisible = false
      this.userMenuVisible = false
      this.languageMenuVisible = false
      this.mobileExchangeMenuVisible = false
      const dropdown = this.$refs.languageDropdown
      const instance = Array.isArray(dropdown) ? dropdown[0] : dropdown
      if (instance && typeof instance.hide === 'function') instance.hide()
      const exchangeDropdown = this.$refs.mobileExchangeDropdown
      const exchangeInstance = Array.isArray(exchangeDropdown) ? exchangeDropdown[0] : exchangeDropdown
      if (exchangeInstance && typeof exchangeInstance.hide === 'function') exchangeInstance.hide()
    },
    notificationTone(item) {
      const type = Number(item && item.type)
      if (type === 2 || type === 4) return 'warning'
      if (type === 7) return 'primary'
      return 'success'
    },
    notificationMobilePreview(item) {
      const raw = (item && item.raw) || {}
      const content = String((item && item.content) || raw.content || '')
      const mediaType = String(
        raw.content_type || raw.contentType || raw.media_type || raw.mediaType || raw.file_type || ''
      ).toLowerCase()
      const isVideo = /video|视频/i.test(mediaType)
        || /<(?:video|source)\b|youtube\.com|youtu\.be|vimeo\.com|\.(?:mp4|webm|mov|m4v|avi|mkv)(?:["'?#\s]|$)/i.test(content)
      if (isVideo) return this.$t('【视频】')
      const isImage = /image|img|picture|图片/i.test(mediaType)
        || /<img\b|data:image\/|\.(?:png|jpe?g|gif|webp|bmp|svg)(?:["'?#\s]|$)/i.test(content)
      if (isImage) return this.$t('【图片】')
      return String((item && item.preview) || '').trim() || this.$t('点击查看通知详情')
    },
    selectNotification(item) {
      this.notificationVisible = false
      this.$emit('notification', item)
    }
  }
}
</script>

<style scoped>
.app-header {
  position: sticky;
  top: 0;
  z-index: 90;
  border-bottom: 1px solid #e9ebf2;
  background: #fff;
}

.app-header__inner {
  display: flex;
  height: 64px;
  align-items: center;
  gap: 28px;
}

.app-header__nav {
  display: flex;
  align-self: stretch;
  gap: 6px;
}

.app-header__nav button {
  position: relative;
  padding: 0 16px;
  color: #4a5060;
  border: 0;
  background: transparent;
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
}

.app-header__nav button::after {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 3px;
  content: "";
  background: transparent;
}

.app-header__nav button.active {
  color: var(--up-blue);
}

.app-header__nav button.active::after {
  background: var(--up-blue);
}

.app-header__tools,
.app-header__auth {
  display: flex;
  margin-left: auto;
  align-items: center;
  gap: 10px;
}

.app-header__download {
  display: inline-flex;
  height: 38px;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 13px;
  color: #1f6bff;
  border: 1px solid #bcd0ff;
  border-radius: 8px;
  background: #f5f8ff;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  transition: color .16s ease, border-color .16s ease, background-color .16s ease;
}

.app-header__download:hover,
.app-header__download:focus-visible {
  color: #fff;
  border-color: #1f6bff;
  outline: none;
  background: #1f6bff;
}

.app-header__mobile-nav-dropdown {
  display: none;
}

.app-header__mobile-exchange-dropdown {
  display: none;
}

.app-header__mobile-nav-trigger {
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  color: #3a3f4e;
  border: 1px solid #dfe3ec;
  border-radius: 8px;
  background: #fff;
  font-size: 17px;
  transition: color .16s ease, border-color .16s ease, background-color .16s ease;
}

.app-header__mobile-nav-trigger:hover,
.app-header__mobile-nav-trigger:focus-visible {
  color: #1f6bff;
  border-color: #a9c4ff;
  outline: none;
  background: #f5f8ff;
}

.app-header__search {
  display: flex;
  width: 210px;
  height: 38px;
  align-items: center;
  gap: 7px;
  padding: 0 12px;
  border: 1px solid #dfe3ec;
  border-radius: 8px;
  background: #fff;
}

.app-header__search i {
  color: #98a0af;
}

.app-header__search input {
  width: 100%;
  padding: 0;
  outline: none;
  color: var(--up-ink);
  border: 0;
  background: transparent;
  font-size: 13px;
}

.app-header__exchange,
.app-header__icon,
.app-header__user,
.app-header__language {
  height: 38px;
  color: var(--up-ink-2);
  border: 1px solid #dfe3ec;
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
  font-weight: 700;
}

.app-header__exchange {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
  padding: 0 13px;
  white-space: nowrap;
}

.app-header__exchange:disabled {
  cursor: wait;
  opacity: .72;
}

::v-deep .exchange-option--active {
  color: #1f6bff;
  font-weight: 700;
}

::v-deep .exchange-option__placeholder {
  display: inline-block;
  width: 14px;
}

.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #12b76a;
}

.app-header__icon {
  position: relative;
  width: 38px;
  font-size: 17px;
}

.app-header__badge {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 7px;
  height: 7px;
  border: 1.5px solid #fff;
  border-radius: 50%;
  background: #e5484d;
}

.app-header__user {
  display: flex;
  width: 118px;
  min-width: 118px;
  flex: 0 0 118px;
  align-items: center;
  gap: 7px;
  padding: 0 8px 0 7px;
  border-color: #dce2ec;
  border-radius: 8px;
  transition: border-color .16s ease, background-color .16s ease;
}

.app-header__user:hover,
.app-header__user:focus-visible,
.app-header__user[aria-expanded="true"] {
  border-color: #a9c4ff;
  outline: none;
  background: #f5f8ff;
}

.app-header__language {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 6px;
  padding: 0 11px;
  white-space: nowrap;
}

.app-header__language-icon {
  width: 15px;
  height: 15px;
  object-fit: contain;
}

.app-header__identity {
  display: flex;
  overflow: hidden;
  min-width: 0;
  align-items: flex-start;
  flex-direction: column;
  line-height: 1;
}

.app-header__identity b {
  display: block;
  overflow: hidden;
  width: 100%;
  color: #1b2230;
  font-size: 11.5px;
  font-weight: 700;
  line-height: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app-header__identity small {
  display: inline-flex;
  height: 11px;
  margin-top: 2px;
  padding: 0 7px;
  align-items: center;
  justify-content: center;
  color: #795600;
  border-radius: 4px;
  background: #f3cd57;
  font-size: 8px;
  font-weight: 800;
  line-height: 11px;
  letter-spacing: .35px;
}

.app-header__avatar {
  display: grid;
  overflow: hidden;
  width: 27px;
  height: 27px;
  flex: 0 0 27px;
  place-items: center;
  color: #fff;
  border-radius: 8px;
  background: linear-gradient(135deg, #4a8cff, #1252e0);
  font-size: 12px;
  font-weight: 800;
}

.app-header__avatar img,
.app-header__menu-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.app-header__user-menu {
  padding: 8px;
}

.app-header__user-summary {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
  padding: 10px 10px 12px;
  border-bottom: 1px solid #f3f4f8;
}

.app-header__user-summary > span:last-child,
.app-header__user-summary b,
.app-header__user-summary small {
  display: block;
  min-width: 0;
}

.app-header__user-summary b {
  overflow: hidden;
  color: #101828;
  font-size: 13.5px;
  font-weight: 800;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app-header__user-summary small {
  overflow: hidden;
  margin-top: 2px;
  color: #98a2b3;
  font-size: 11.5px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app-header__menu-avatar {
  display: grid;
  overflow: hidden;
  width: 38px;
  height: 38px;
  flex: 0 0 auto;
  place-items: center;
  color: #1f6bff;
  border-radius: 50%;
  background: #e4eefe;
  font-size: 15px;
  font-weight: 800;
}

.app-header__user-menu-item {
  display: flex;
  width: 100%;
  padding: 11px 10px;
  align-items: center;
  gap: 10px;
  color: #344054;
  border: 1px solid transparent;
  border-radius: 9px;
  background: transparent;
  text-align: left;
  transition: border-color .16s ease, background-color .16s ease;
}

.app-header__user-menu-item:hover,
.app-header__user-menu-item:focus-visible {
  border-color: #dce8fd;
  outline: none;
  background: #f5f6f9;
}

.app-header__user-menu-item--api {
  margin-bottom: 6px;
  border-color: #dce8fd;
  background: #f0f5fe;
}

.app-header__user-menu-item--api:hover,
.app-header__user-menu-item--api:focus-visible {
  border-color: #dce8fd;
  background: #e4eefe;
}

.app-header__menu-icon {
  display: grid;
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
  place-items: center;
  color: #667085;
  border-radius: 8px;
  background: #f5f6f9;
}

.app-header__menu-icon--api {
  color: #fff;
  background: #1f6bff;
}

.app-header__menu-icon--logout {
  color: #e5484d;
  background: #fff1f0;
  font-size: 15px;
}

.app-header__user-menu-item--logout {
  margin-top: 4px;
  border-top: 1px solid #f3f4f8;
  border-radius: 0 0 9px 9px;
}

.app-header__user-menu-item--logout .app-header__menu-copy strong {
  color: #e5484d;
}

.app-header__user-menu-item--logout:hover,
.app-header__user-menu-item--logout:focus-visible {
  border-color: #ffd8d6;
  background: #fff6f5;
}

.app-header__menu-icon img {
  display: block;
  width: 15px;
  height: 15px;
}

.app-header__menu-copy {
  min-width: 0;
  flex: 1;
}

.app-header__menu-copy strong,
.app-header__menu-copy small {
  display: block;
  white-space: nowrap;
}

.app-header__menu-copy strong {
  color: #3a3f4e;
  font-size: 13px;
  font-weight: 800;
  line-height: 19px;
}

.app-header__user-menu-item--api .app-header__menu-copy strong {
  color: #17408f;
}

.app-header__menu-copy small {
  color: #6e86b8;
  font-size: 11.5px;
  line-height: 17px;
}

.app-header__binding-status {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  color: #067647;
  border-radius: 999px;
  background: #e9f8ef;
  font-size: 11px;
  font-weight: 800;
  line-height: 14px;
  white-space: nowrap;
}

.app-header__binding-status i {
  width: 6px;
  height: 6px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: #12b76a;
}

.app-header__menu-arrow {
  margin-left: auto;
  color: #b0b7c3;
  font-size: 12px;
}

.notification__title {
  display: flex;
  min-height: 48px;
  padding: 0 16px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid #eef0f5;
}

.notification__title strong {
  color: #1b1f2a;
  font-size: 14px;
  font-weight: 800;
}

.notification__title span {
  color: #e5484d;
  font-size: 11px;
}

.notification__item {
  display: flex;
  width: calc(100% - 16px);
  overflow: hidden;
  gap: 10px;
  margin: 4px 8px;
  padding: 10px 8px;
  align-items: flex-start;
  color: inherit;
  border: 0;
  border-radius: 8px;
  background: transparent;
  text-align: left;
}

.notification__item:hover {
  background: #f5f7fb;
}

.notification__item strong,
.notification__item small,
.notification__item time {
  display: block;
}

.notification__content {
  overflow: hidden;
  min-width: 0;
  flex: 1;
}

.notification__item strong {
  overflow: hidden;
  color: #4b5362;
  font-size: 13px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notification__mobile-copy {
  display: none;
}
.notification__preview {
  overflow: hidden;
  margin-top: 3px;
  color: #98a0af;
  font-size: 11.5px;
  line-height: 17px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notification__time {
  overflow: hidden;
  margin-top: 2px;
  color: #aab1bf;
  font-size: 10.5px;
  font-style: normal;
  line-height: 15px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notification__arrow {
  margin-top: 6px;
  flex: 0 0 auto;
  color: #b0b7c3;
  font-size: 11px;
}

.notification__dot {
  width: 7px;
  height: 7px;
  margin-top: 5px;
  flex: 0 0 auto;
  border-radius: 50%;
}

.notification__dot.warning {
  background: #f59e0b;
}

.notification__dot.primary {
  background: #1f6bff;
}

.notification__dot.success {
  background: #12b76a;
}

.notification__state {
  display: flex;
  min-height: 104px;
  align-items: center;
  justify-content: center;
  gap: 7px;
  color: #98a0af;
  font-size: 12px;
}

.notification__more {
  display: flex;
  width: 100%;
  height: 42px;
  align-items: center;
  justify-content: center;
  gap: 3px;
  color: #1f6bff;
  border: 0;
  border-top: 1px solid #eef0f5;
  background: #fff;
  font-size: 12.5px;
  font-weight: 700;
  text-decoration: none;
}

.notification__more:hover {
  background: #f7f9fd;
}
.app-header-sheet__heading,
.notification__title-mobile,
.notification__read-all,
.app-header__user-menu-item--mobile,
.app-header__sheet-backdrop {
  display: none;
}

.app-header__menu-value {
  overflow: hidden;
  max-width: 88px;
  margin-left: auto;
  color: #98a2b3;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 1100px) {
  .app-header__download {
    width: 38px;
    padding: 0;
  }

  .app-header__download span {
    display: none;
  }

  .app-header__search {
    display: none;
  }

  .app-header__tools .app-header__language {
    display: none;
  }
}

@media (max-width: 860px) {
  .app-header__inner {
    height: 64px;
    min-height: 64px;
    flex-wrap: nowrap;
    gap: 8px;
    padding-top: 0;
    padding-bottom: 0;
  }

  .app-header__nav {
    display: none;
  }

  .app-header__tools {
    gap: 7px;
  }

  .app-header__tools .app-header__exchange {
    display: none;
  }

  .app-header__tools .app-header__identity {
    display: flex;
  }

  .app-header__mobile-nav-dropdown {
    display: block;
    flex: 0 0 auto;
  }

  .app-header__auth {
    display: none;
  }

  .app-header__auth + .app-header__mobile-nav-dropdown {
    margin-left: auto;
  }

  .app-header__tools + .app-header__mobile-nav-dropdown {
    margin-left: 0;
  }

  .notification__title {
    border-bottom: unset !important;
  }
}

@media (max-width: 360px) {
  .app-header__tools {
    gap: 5px;
  }

  .app-header__user {
    width: 100px;
    min-width: 100px;
    flex-basis: 100px;
  }

  .app-header__identity b {
    font-size: 10.5px;
  }
}
</style>

<style>
.app-header-user-popover.el-popover {
  padding: 0;
  border: 1px solid #e9ebf2;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(23, 20, 45, 0.12);
}

.app-header-user-popover.el-popper[x-placement^="bottom"] {
  margin-top: 2px !important;
}

.app-header-user-popover .popper__arrow {
  display: none;
}

.app-header-notification-popover.el-popover {
  overflow: hidden;
  padding: 0;
  border: 1px solid #e9ebf2;
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(23, 20, 45, 0.12);
}

.app-header-notification-popover.el-popper[x-placement^="bottom"] {
  margin-top: 2px !important;
}

.app-header-language-menu.el-dropdown-menu {
  min-width: 148px;
  padding: 6px;
  border: 1px solid #e5e9f1;
  border-radius: 10px;
  box-shadow: 0 10px 24px rgba(23, 32, 52, .12);
}

.app-header-language-menu .el-dropdown-menu__item {
  display: flex;
  height: 40px;
  padding: 0 12px;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  color: #3a3f4e;
  border-radius: 7px;
  font-size: 14px;
  font-weight: 400;
  line-height: 40px;
}

.app-header-language-menu .el-dropdown-menu__item:hover,
.app-header-language-menu .el-dropdown-menu__item:focus,
.app-header-language-menu .el-dropdown-menu__item.is-active {
  color: #1f6bff;
  background: #eef4ff;
}

.app-header-language-menu .el-dropdown-menu__item i {
  margin: 0;
  color: #1f6bff;
}

.app-header-mobile-nav-menu.el-dropdown-menu {
  min-width: 178px;
  padding: 6px;
  border: 1px solid #e5e9f1;
  border-radius: 10px;
  box-shadow: 0 12px 30px rgba(23, 32, 52, .14);
}

.app-header-mobile-nav-menu .el-dropdown-menu__item {
  display: flex;
  min-height: 42px;
  padding: 0 12px;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  color: #3a3f4e;
  border-radius: 7px;
  font-size: 14px;
  font-weight: 600;
  line-height: 42px;
}

.app-header-mobile-nav-menu .el-dropdown-menu__item:hover,
.app-header-mobile-nav-menu .el-dropdown-menu__item:focus,
.app-header-mobile-nav-menu .el-dropdown-menu__item.is-active {
  color: #1f6bff;
  background: #eef4ff;
}

.app-header-mobile-nav-menu .el-dropdown-menu__item i {
  margin: 0;
  color: #1f6bff;
}

.app-header-mobile-nav-menu .app-header-mobile-nav-menu__action {
  box-sizing: border-box;
  padding-right: 12px;
  padding-left: 12px;
  align-items: center;
  justify-content: space-between;
  text-indent: 0;
}

.app-header-mobile-nav-menu .app-header-mobile-nav-menu__action > span {
  flex: 1 1 auto;
  margin: 0;
  text-align: left;
}

.app-header-mobile-nav-menu .app-header-mobile-nav-menu__action > i {
  flex: 0 0 auto;
  margin: 0 0 0 12px;
}

.app-header-mobile-nav-menu .app-header-mobile-nav-menu__action--download {
  margin-top: 6px;
  border-top: 1px solid #e5e9f1;
}

.app-header-mobile-nav-menu .el-dropdown-menu__item.mobile-exchange-title {
  cursor: default;
  color: #667085;
  background: #f7f9fc;
  opacity: 1;
}

.app-header-mobile-nav-menu .mobile-exchange-title span {
  font-size: 12px;
  font-weight: 600;
}

.app-header-mobile-nav-menu .mobile-exchange-title strong {
  overflow: hidden;
  max-width: 124px;
  margin-left: auto;
  color: #1f2937;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app-header-mobile-nav-menu .el-dropdown-menu__item.mobile-exchange-option {
  justify-content: flex-start;
}

.app-header-mobile-nav-menu .mobile-exchange-option .status-dot {
  width: 7px;
  height: 7px;
  flex: 0 0 7px;
  margin-right: 2px;
  border-radius: 50%;
  background: #12b76a;
}

.app-header-mobile-nav-menu .mobile-exchange-option i {
  margin-left: auto;
}

.app-header-mobile-nav-menu .el-dropdown-menu__item.mobile-exchange-option.exchange-option--active,
.app-header-mobile-nav-menu .el-dropdown-menu__item.mobile-exchange-option.exchange-option--active.is-disabled {
  color: #1f6bff;
  background: #eef4ff;
  opacity: 1;
}

.app-header-mobile-nav-menu .el-dropdown-menu__item.mobile-exchange-retry {
  justify-content: flex-start;
  color: #1f6bff;
}

.app-header-mobile-nav-menu .mobile-exchange-retry i {
  margin-right: 2px;
}

.app-header-mobile-nav-menu .el-dropdown-menu__item.mobile-exchange-empty {
  cursor: default;
  justify-content: center;
  color: #98a2b3;
  opacity: 1;
}

.app-header-mobile-nav-menu .el-dropdown-menu__item.is-primary {
  color: #1f6bff;
}

</style>
